'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { ValueOf } from 'type-fest';
import { useCountDown } from 'ahooks';
import { useTranslations } from 'next-intl';
import {
  CloseButton,
  FieldSet,
  Heading,
  InlineFeedback,
  Legend,
  Paragraph,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation-10';
import { HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import type { HpUserCredentialsDto } from '@zg-rentals/ts-hotpads-api-client';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { useSendOtp, useVerifyOtp } from '@/src/lib/hooks/query';
import { CodeInput, DEFAULT_CODE_LENGTH } from '@/src/components/CodeInput';
import { GatedButton } from '@/src/components/ui/GatedButton';
import { maskEmail } from '@/src/lib/auth/email';
import { feedback, footerRow, header, resendRow, stack } from './otpSignIn.styles';

export const RESEND_COOLDOWN_MS = 30_000;

export type OtpSignInFormProps = {
  /** Address the code is sent to. Owned by the caller — this form never edits it. */
  email: string;
  onBack?: () => void;
  onClose?: () => void;
  onVerified: (credentials: HpUserCredentialsDto) => void;
  /**
   * The caller's own send already hit the per-address budget. Starts the step in the same
   * state a rejected resend would: no further sends, but code entry stays open, because a
   * code already in the inbox is still redeemable.
   */
  sendLimitReached?: boolean;
  /**
   * Heading for the form. Omit on surfaces that supply their own chrome — the modal
   * renders it in `Modal.Header` so it sits where every other modal's title does.
   */
  title?: string;
};

const VERIFY_ERROR_COPY = {
  [HpApiWebApiError.INVALID_CREDENTIALS]: 'otpErrorInvalidCode',
  [HpApiWebApiError.CODE_EXPIRED]: 'otpErrorExpired',
  // Retires the code that was guessed at, not the address: the attempt tally is keyed on
  // the code hash and expires with it, so a new code grants a fresh set of attempts.
  [HpApiWebApiError.CODE_ATTEMPTS_EXCEEDED]: 'otpErrorTooManyAttempts',
  // sendOtp only: the per-address send budget is spent. A code already in the inbox stays
  // valid, so this must not block verifying.
  [HpApiWebApiError.SEND_LIMIT_EXCEEDED]: 'otpErrorSendLimit',
  // Generic throttle from the shared handler rather than the OTP flow itself.
  [HpApiWebApiError.TOO_MANY_REQUESTS]: 'otpErrorSendLimit',
  // Retrying never succeeds, so the generic "try again" copy would be actively wrong.
  [HpApiWebApiError.ACCOUNT_DEACTIVATED]: 'otpErrorAccountDeactivated',
  // Everything else falls back to the generic error on purpose. A dedicated message earns
  // its place only by naming a different next step: invalidEmail cannot be reached from here
  // (the address is validated before this step mounts), and the v3EndpointsEnabled kill
  // switch's 404 and a session signed in by another tab both amount to "try again later".
} as const satisfies Partial<Record<HpApiWebApiError, string>>;

type OtpMessageKey = ValueOf<typeof VERIFY_ERROR_COPY> | 'errorGeneric' | 'otpErrorSendFailed';

/** Resolves a v3 reason to its copy key, falling back when the reason has none of its own. */
const messageKeyFor = (reason: HpApiWebApiError | undefined, fallback: OtpMessageKey): OtpMessageKey =>
  reason && reason in VERIFY_ERROR_COPY ? VERIFY_ERROR_COPY[reason as keyof typeof VERIFY_ERROR_COPY] : fallback;

// Retires the current code server-side: further digits cannot succeed against it, so stop
// accepting verify attempts until a new code replaces it.
const CODE_SPENT_REASONS = new Set<HpApiWebApiError>([HpApiWebApiError.CODE_ATTEMPTS_EXCEEDED]);

// Exhausts the address's send budget for the window, so no amount of waiting out the
// cooldown will help.
const SEND_BLOCKED_REASONS = new Set<HpApiWebApiError>([
  HpApiWebApiError.SEND_LIMIT_EXCEEDED,
  HpApiWebApiError.TOO_MANY_REQUESTS,
]);

/**
 * Email one-time-code sign-in.
 *
 * Collects the code, verifies it, and resends on request; on success the session is live
 * and the current-user cache already reflects it, so the caller only has to decide what
 * happens next. The caller sends the first code — see the mount effect below for why this
 * cannot do it. Renders no chrome of its own beyond an optional title, and never offers
 * social or password sign-in.
 */
export function OtpSignInForm({ email, onBack, onClose, onVerified, sendLimitReached, title }: OtpSignInFormProps) {
  const t = useTranslations('auth');
  const sendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState(sendLimitReached ? t('otpErrorSendLimit') : '');
  const [entryLocked, setEntryLocked] = useState(false);
  const [resendConfirmed, setResendConfirmed] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number>();
  // The per-address send budget is spent; no cooldown will bring it back this hour.
  const [sendBlocked, setSendBlocked] = useState(Boolean(sendLimitReached));

  const [cooldownRemaining] = useCountDown({ targetDate: cooldownUntil });

  // Guards the auto-submit so a rejected code is not retried on every keystroke.
  const attemptedCodeRef = useRef('');

  const feedbackId = useId();
  const idPrefix = useId();

  /**
   * Starts the resend cooldown. Called when a code has actually gone out, never when one
   * is merely requested — measuring from the request would let the countdown expire while
   * the send is still in flight, leaving the control reading "Sending…" at zero seconds.
   */
  const startCooldown = () => setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);

  const requestCode = (isResend: boolean) => {
    // The control below is disabled too, but the cooldown exists to protect a write
    // rate-limited endpoint — too load-bearing to leave to the button's styling.
    if (isResend && (sendOtp.isPending || cooldownRemaining > 0 || sendBlocked)) {
      return;
    }
    setErrorMessage('');
    setResendConfirmed(false);

    sendOtp.mutate(
      { postData: { email } },
      {
        onSuccess: () => {
          startCooldown();
          if (isResend) {
            // Reached either way: sendOtp answers identically whether or not the address
            // has an account, and the UI must not narrow that down.
            setResendConfirmed(true);
            setCode('');
            setEntryLocked(false);
            attemptedCodeRef.current = '';
          }
        },
        onError: (error) => {
          // No cooldown to undo: it only starts once a code is actually out, so a failed
          // send leaves the button immediately available rather than stranding the user on
          // an error that tells them to request a new one.
          const reason = v3ErrorReason(error);
          setErrorMessage(t(messageKeyFor(reason, 'otpErrorSendFailed')));
          if (reason && SEND_BLOCKED_REASONS.has(reason)) {
            // Deliberately does not lock verification: a code already sitting in the inbox
            // is still redeemable, and the budget only caps new sends.
            setSendBlocked(true);
          }
        },
      },
    );
  };

  useEffect(() => {
    // The caller sent the first code, so the cooldown starts here rather than after a
    // send of our own. Deliberately not firing sendOtp on mount: useMutation subscribes
    // its observer in an effect, and StrictMode's mount/cleanup/mount cycle orphans a
    // mutation started during that first pass — the request goes out but its result
    // never reaches the observer, leaving isPending stuck on forever. Resend is safe
    // because it is click-driven, by which point the observer is settled.
    startCooldown();
  }, []);

  const submitCode = (codeToVerify: string) => {
    if (entryLocked || verifyOtp.isPending || codeToVerify.length !== DEFAULT_CODE_LENGTH) {
      return;
    }
    attemptedCodeRef.current = codeToVerify;
    setErrorMessage('');
    setResendConfirmed(false);

    verifyOtp.mutate(
      { postData: { code: codeToVerify, email } },
      {
        onSuccess: (response) => {
          if (!response.data) {
            setErrorMessage(t('otpErrorSendFailed'));
            return;
          }
          onVerified(response.data);
        },
        onError: (error) => {
          const reason = v3ErrorReason(error);
          setErrorMessage(t(messageKeyFor(reason, 'errorGeneric')));
          if (reason && CODE_SPENT_REASONS.has(reason)) {
            setEntryLocked(true);
            return;
          }
          // Retrying a six-box code should not start with six backspaces. `attemptedCodeRef`
          // deliberately keeps the rejected code, so retyping the same digits does not
          // auto-submit and burn another attempt.
          setCode('');
        },
      },
    );
  };

  const handleCodeChange = (next: string) => {
    setCode(next);
    if (errorMessage && !entryLocked) {
      setErrorMessage('');
    }
    // Autofill and paste deliver all six digits at once; submitting on completion saves
    // the extra tap without stealing the choice from someone typing.
    if (next.length === DEFAULT_CODE_LENGTH && next !== attemptedCodeRef.current) {
      submitCode(next);
    }
  };

  const isComplete = code.length === DEFAULT_CODE_LENGTH;
  const isVerifying = verifyOtp.isPending;
  const canSubmit = isComplete && !isVerifying && !entryLocked;
  const resendCooldownSeconds = Math.ceil(cooldownRemaining / 1000);
  const resendDisabled = sendOtp.isPending || resendCooldownSeconds > 0 || sendBlocked;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit) {
      submitCode(code);
    }
  };

  const gateReason = () => {
    if (isVerifying) return undefined;
    if (entryLocked) return errorMessage;
    if (!isComplete) return t('otpGateHintCode');
    return undefined;
  };

  return (
    <div className={stack}>
      {(title || onClose) && (
        <div className={header}>
          {title && (
            <Heading level={2} textStyle="heading-xs">
              {title}
            </Heading>
          )}
          {onClose && <CloseButton type="button" title={t('otpClose')} onClick={onClose} />}
        </div>
      )}

      <Paragraph>{t('otpDescription', { email: maskEmail(email) })}</Paragraph>

      <form className={stack} onSubmit={handleSubmit} noValidate>
        <FieldSet
          legend={<Legend>{t('otpCodeLegend')}</Legend>}
          error={Boolean(errorMessage)}
          // FieldSet associates this with every nested control via aria-describedby, so the
          // boxes do not need to be wired up by hand. Always rendered, empty included: a
          // live region that appears at the same moment its text does is not reliably
          // announced.
          description={
            <span id={feedbackId} aria-live="polite" aria-atomic="true" className={feedback}>
              {/* Compound form so the label can be sized: the shorthand renders body (16px),
                  which sits oddly next to the 14px legend above. Set via `css` rather than the
                  `textStyle` prop — the label merges `css` after its own recipe, so the prop
                  alone loses to the recipe's body. */}
              {errorMessage && (
                <InlineFeedback.Root tone="critical" role="alert">
                  <InlineFeedback.Icon />
                  <InlineFeedback.Label css={{ textStyle: 'body-sm' }}>{errorMessage}</InlineFeedback.Label>
                </InlineFeedback.Root>
              )}
              {!errorMessage && resendConfirmed && (
                <InlineFeedback.Root tone="success">
                  <InlineFeedback.Icon />
                  <InlineFeedback.Label css={{ textStyle: 'body-sm' }}>
                    {t('otpResendConfirmation')}
                  </InlineFeedback.Label>
                </InlineFeedback.Root>
              )}
            </span>
          }
        >
          <CodeInput
            idPrefix={idPrefix}
            digitLabel={(position, total) => t('otpDigitLabel', { position, total })}
            value={code}
            onChange={handleCodeChange}
            disabled={isVerifying || entryLocked}
            error={Boolean(errorMessage)}
          />
        </FieldSet>

        {/* Constellation renders the label transparent under `loading` and overlays the
            spinner, so the in-flight state has to be announced separately — swapping the
            label would change the accessible name but show nobody anything. Always
            rendered, empty included: a live region that appears at the same moment its text
            does is not reliably announced. */}
        <VisuallyHidden role="status" aria-live="polite">
          {isVerifying ? t('otpVerifying') : ''}
        </VisuallyHidden>

        <GatedButton
          type="submit"
          tone="brand"
          emphasis="filled"
          size="lg"
          fluid
          disabled={!canSubmit}
          loading={isVerifying}
          gateReason={gateReason()}
        >
          {t('otpContinue')}
        </GatedButton>
      </form>

      <div className={resendRow}>
        <TextButton type="button" onClick={() => requestCode(true)} disabled={resendDisabled}>
          {/* Sending, cooling down, and ready are mutually exclusive: the cooldown does not
              begin until a code is out, so the countdown can never reach zero mid-send and
              strand the control on a progress label. */}
          {sendOtp.isPending
            ? t('otpResending')
            : resendCooldownSeconds > 0
              ? t('otpResendCooldown', { seconds: resendCooldownSeconds })
              : t('otpResend')}
        </TextButton>
      </div>

      {onBack && (
        <div className={footerRow}>
          <TextButton type="button" onClick={onBack}>
            {t('otpBack')}
          </TextButton>
        </div>
      )}
    </div>
  );
}
